import { useEffect, useRef, useState, useCallback } from 'react';

// useActivityNotifications — feed de notificacoes transitorias (estilo
// "toast") derivado das transicoes de status POR MAQUINA (thing) entre
// ticks de polling. Diferente da zona "Crítico" do AlertOverlay (que e
// uma foto do estado atual e persiste enquanto durar o alarme), aqui
// registramos EVENTOS de transicao que somem sozinhos depois de um tempo:
//   - comecou a rodar (qualquer -> RODANDO, vindo de desligada/sem-dados)
//   - recuperou (ALARME/ATENCAO -> RODANDO)
//   - desligou (-> DESLIGADA)
//   - ficou sem dados (-> SEM_DADOS)
//
// Transicoes PARA alarme/atencao NAO entram aqui — a zona Crítico ja
// cobre. Fonte do status por-thing: marker.appliance[].thing[].status
// ({ id, name, color }), agora ao vivo no payload de parks.

const TTL_MS = 60 * 1000; // notificacao some 60s apos a transicao
const MAX_ITEMS = 8; // teto pra nao virar parede de texto
const SWEEP_MS = 5000; // frequencia do sweep de expiracao

// Historico persistente: o toast some em 60s (glance), mas o evento fica
// registrado aqui pra responder "o que aconteceu enquanto eu nao olhava?".
// Buffer rolante em localStorage — sobrevive a F5 e a sessao do painel TV.
const HISTORY_MAX = 50;
const HISTORY_KEY = 'cco:activity-history';

const loadHistory = () => {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, HISTORY_MAX) : [];
  } catch (e) {
    // localStorage indisponivel (modo privado/quota) — degrada pra memoria.
    return [];
  }
};

const persistHistory = (list) => {
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
  } catch (e) {
    // Silencioso: persistencia e best-effort; o feed continua em memoria.
  }
};

// Normaliza o name do StatusType pra uma chave primaria, tolerante a
// acento / variacao de label ('Rodando'/'Operando', 'Sem Dados', etc).
const normalizeStatus = (name) => {
  // Apenas lowercase: os trechos testados ('aten', 'deslig', 'sem dado'...)
  // ja casam mesmo com acento ('atenção'.includes('aten') === true), entao
  // nao precisamos stripar diacriticos (evita regex de combining chars que
  // alguns editores/encodings no Windows mastigam).
  const n = (name || '').toLowerCase();
  if (n.includes('alarme') || n.includes('erro') || n.includes('bloque'))
    return 'alarme';
  if (n.includes('rodando') || n.includes('operando')) return 'rodando';
  if (n.includes('deslig')) return 'desligada';
  if (n.includes('aten')) return 'atencao';
  if (n.includes('sem dado') || n.includes('semdados') || n.includes('sem-dado'))
    return 'sem-dados';
  return 'sem-dados';
};

// Mapeia a transicao (prev -> curr) pra uma notificacao. Retorna null
// quando a transicao nao deve virar atividade (ex.: -> alarme/atencao,
// coberto pela zona Crítico; ou status sem mudanca real).
const transitionFor = (prev, curr) => {
  if (prev === curr) return null;
  if (curr === 'rodando') {
    if (prev === 'alarme' || prev === 'atencao')
      return { kind: 'recuperou', label: 'recuperou', color: 'var(--cco-green)' };
    return {
      kind: 'rodando',
      label: 'começou a rodar',
      color: 'var(--cco-green)',
    };
  }
  if (curr === 'desligada')
    return { kind: 'desligada', label: 'desligou', color: 'var(--cco-ink-mute)' };
  if (curr === 'sem-dados')
    return {
      kind: 'sem-dados',
      label: 'ficou sem dados',
      color: 'var(--cco-ink-faint)',
    };
  // curr === 'alarme' | 'atencao' -> nao gera atividade
  return null;
};

// onNewEvents (opcional): callback chamado a cada tick que gera transicoes,
// com o batch de eventos novos (fresh). Usado pra cues sonoros (ex.: perda
// de comunicacao) sem acoplar audio ao hook. Guardado em ref pra nao entrar
// nas deps do effect (evita re-rodar quando a closure do callback muda).
export const useActivityNotifications = (markers, onNewEvents) => {
  // prevRef: Map<thingId, { status, thingName, parkName }> do tick anterior.
  // null = baseline ainda nao capturado (primeiro load nao dispara nada).
  const prevRef = useRef(null);
  const onNewEventsRef = useRef(onNewEvents);
  onNewEventsRef.current = onNewEvents;
  const [items, setItems] = useState([]);
  // Historico persistente (nao expira; rolante em HISTORY_MAX). Cada evento
  // e uma entrada distinta (chave unica id+ts) — diferente do feed ao vivo,
  // que deduplica por maquina.
  const [history, setHistory] = useState(loadHistory);

  useEffect(() => {
    if (!Array.isArray(markers)) return;

    const curr = new Map();
    markers.forEach((park) => {
      const parkName = park && park.name;
      const appliances = (park && park.appliance) || [];
      appliances.forEach((app) => {
        const things = (app && app.thing) || [];
        things.forEach((th) => {
          if (!th || th.id == null) return;
          curr.set(th.id, {
            status: normalizeStatus(th.status && th.status.name),
            thingName: th.thing || th.name || 'Máquina',
            parkName,
          });
        });
      });
    });

    const prev = prevRef.current;
    prevRef.current = curr;
    // Baseline silencioso: no primeiro tick so registramos, nao notificamos
    // (senao floda com "N maquinas rodando" toda vez que a tela monta).
    if (prev === null) return;

    const fresh = [];
    curr.forEach((c, id) => {
      const p = prev.get(id);
      if (!p) return; // thing nova nesse tick — nao e transicao
      const t = transitionFor(p.status, c.status);
      if (t) {
        fresh.push({
          id, // chave = thingId: uma notificacao por maquina por vez
          thingName: c.thingName,
          parkName: c.parkName,
          kind: t.kind,
          label: t.label,
          color: t.color,
          ts: Date.now(),
        });
      }
    });

    if (!fresh.length) return;

    // Avisa o consumidor (cues sonoros etc.) com o batch novo deste tick.
    if (typeof onNewEventsRef.current === 'function') {
      onNewEventsRef.current(fresh);
    }

    // Acumula no historico persistente (mais recentes no topo, sem dedup —
    // cada transicao e um registro). Persiste em localStorage.
    setHistory((prev) => {
      const entries = fresh.map((f) => ({ ...f, key: `${f.id}-${f.ts}` }));
      const next = [...entries, ...prev].slice(0, HISTORY_MAX);
      persistHistory(next);
      return next;
    });

    setItems((existing) => {
      // Dedup por thing: nova transicao SUBSTITUI a anterior daquela
      // maquina (e reseta o ts/expiracao) — maquina que flapa nao empilha
      // varios cards, atualiza o seu unico card.
      const byId = new Map(existing.map((e) => [e.id, e]));
      fresh.forEach((f) => byId.set(f.id, f));
      let next = Array.from(byId.values()).sort((a, b) => b.ts - a.ts);
      if (next.length > MAX_ITEMS) next = next.slice(0, MAX_ITEMS);
      return next;
    });
  }, [markers]);

  // Sweep de expiracao: roda enquanto houver itens; remove os que passaram
  // do TTL. Para o interval quando esvazia (sem timer ocioso).
  useEffect(() => {
    if (!items.length) return undefined;
    const interval = setInterval(() => {
      const cutoff = Date.now() - TTL_MS;
      setItems((existing) => {
        const next = existing.filter((e) => e.ts >= cutoff);
        return next.length === existing.length ? existing : next;
      });
    }, SWEEP_MS);
    return () => clearInterval(interval);
  }, [items.length]);

  const dismissActivity = useCallback((id) => {
    setItems((existing) => existing.filter((e) => e.id !== id));
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    persistHistory([]);
  }, []);

  return { activity: items, history, dismissActivity, clearHistory };
};
